<template>
  <div>
    <router-link :to="{ name: 'tenants' }" class="detail__back">
      <span class="material-icons detail__back-icon">arrow_back</span>
      {{ $tc("phrases.backToAllTenants") }}
    </router-link>

    <spinner v-if="loading && !tenant" />

    <template v-else-if="tenant">
      <header class="detail__header">
        <div class="detail__identity">
          <h1 class="detail__title">
            {{ tenant.name }}
            <span class="mpm-chip" :class="`status-chip--${tenant.health}`">
              {{ $tc(`words.${tenant.health}`) }}
            </span>
          </h1>
          <p class="detail__meta">{{ metaLine }}</p>
          <div v-if="tenant.plugins.length > 0" class="detail__plugins">
            <span
              v-for="plugin in tenant.plugins"
              :key="plugin"
              class="mpm-chip mpm-chip--primary"
            >
              {{ plugin }}
            </span>
          </div>
        </div>

        <form class="detail__invoice" @submit.prevent="downloadInvoice">
          <label class="mpm-field detail__invoice-month">
            <span class="mpm-field__label">
              {{ $tc("phrases.invoiceDataMonth") }}
            </span>
            <input
              v-model="invoiceMonth"
              class="mpm-field__input"
              type="month"
              :max="currentMonth"
              required
            />
          </label>
          <button
            type="submit"
            class="mpm-button mpm-button--raised mpm-button--primary"
            :disabled="downloadingInvoice"
          >
            <span class="material-icons">download</span>
            {{ $tc("phrases.downloadInvoiceData") }}
          </button>
        </form>
      </header>

      <kpi-strip :items="kpis" class="detail__kpis" />

      <div class="detail__grid">
        <tenant-transactions-card :monthly="tenant.monthly" />
        <tenant-fleet-health-card :tenant="tenant" />
      </div>

      <tenant-activity-card
        :activity="tenant.activity"
        class="detail__activity"
      />
    </template>
  </div>
</template>

<script>
import { formatCount, formatMoney, formatMonthYear } from "@/Helpers/format.js"
import { notify } from "@/mixins/notify.js"
import { OperatorDashboardService } from "@/services/OperatorDashboardService.js"
import KpiStrip from "@/shared/KpiStrip.vue"
import Spinner from "@/shared/Spinner.vue"
import TenantActivityCard from "@/views/parts/TenantActivityCard.vue"
import TenantFleetHealthCard from "@/views/parts/TenantFleetHealthCard.vue"
import TenantTransactionsCard from "@/views/parts/TenantTransactionsCard.vue"

const monthValue = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`

export default {
  name: "TenantDetail",
  components: {
    KpiStrip,
    Spinner,
    TenantActivityCard,
    TenantFleetHealthCard,
    TenantTransactionsCard,
  },
  mixins: [notify],
  props: {
    tenantId: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    const today = new Date()

    return {
      operatorDashboardService: new OperatorDashboardService(),
      // Invoices are raised for the last complete month.
      invoiceMonth: monthValue(
        new Date(today.getFullYear(), today.getMonth() - 1, 1),
      ),
      currentMonth: monthValue(today),
      downloadingInvoice: false,
    }
  },
  computed: {
    tenant() {
      return this.$store.state.operatorDashboard.tenantDetail
    },
    loading() {
      return this.$store.state.operatorDashboard.loading.detail
    },
    metaLine() {
      return [
        this.tenant.country,
        this.tenant.usageType,
        this.$tc("phrases.registeredIn", 1, {
          month: formatMonthYear(this.tenant.registeredAt),
        }),
        this.tenant.email,
        this.tenant.phone,
      ]
        .filter(Boolean)
        .join(" · ")
    },
    kpis() {
      return [
        {
          label: this.$tc("words.customers"),
          icon: "supervisor_account",
          color: "orange",
          value: formatCount(this.tenant.customers),
        },
        {
          label: this.$tc("words.devices"),
          icon: "settings_input_hdmi",
          color: "red",
          value: formatCount(this.tenant.devices.total),
        },
        {
          label: this.$tc("phrases.thisMonthTransactions"),
          icon: "swap_horiz",
          color: "blue",
          value: formatCount(this.tenant.transactionsThisMonth),
        },
        {
          // The only place money appears: one tenant, in its own currency.
          label: this.$tc("phrases.volumePerMonth"),
          icon: "attach_money",
          color: "green",
          value: formatMoney(this.tenant.volumeThisMonth, this.tenant.currency),
        },
      ]
    },
  },
  watch: {
    tenantId: "load",
  },
  created() {
    this.load()
  },
  methods: {
    async load() {
      try {
        await this.$store.dispatch(
          "operatorDashboard/fetchTenantDetail",
          this.tenantId,
        )
      } catch (e) {
        this.alertNotify("error", e.message)
      }
    },
    async downloadInvoice() {
      this.downloadingInvoice = true
      try {
        const { blob, filename } =
          await this.operatorDashboardService.downloadInvoice(
            this.tenant.id,
            this.invoiceMonth,
          )
        const url = window.URL.createObjectURL(blob)
        const anchor = document.createElement("a")
        anchor.href = url
        anchor.download = filename
        document.body.appendChild(anchor)
        anchor.click()
        anchor.remove()
        window.URL.revokeObjectURL(url)
      } catch (e) {
        this.alertNotify("error", e.message)
      } finally {
        this.downloadingInvoice = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.detail__back {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: $font-cell;
  font-weight: 400;
  color: $brand-primary;
  margin-bottom: 14px;
}

.detail__back-icon {
  font-size: 18px;
}

.detail__header {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 16px 24px;
  margin-bottom: 20px;
}

.detail__identity {
  flex: 1 1 420px;
  min-width: 0;
}

.detail__invoice {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 12px;
}

.detail__invoice-month {
  width: 170px;
}

.detail__title {
  margin: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  font-size: $font-h3;
  line-height: 1.4em;
}

.detail__meta {
  margin: 4px 0 0;
  font-size: $font-cell;
  color: $ops-text-muted;
}

.detail__plugins {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 10px;
}

.detail__kpis {
  margin-bottom: $ops-gap;
}

.detail__grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(380px, 100%), 1fr));
  gap: $ops-gap;
  align-items: start;
  margin-bottom: $ops-gap;
}
</style>
